import React, { useState, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import clsx from "clsx"

import { incNumberOfOrders } from "@redux"
import { haveAuth } from "utils"
import { getOrder } from "services"
import OrderDetailsHeader from "./OrderDetailsHeader"
import OrderDetailsCustomerInfo from "./OrderDetailsCustomerInfo"
import OrderDetailsFooter from "./OrderDetailsFooter"
import OrderDetailsItems from "./OrderDetailsItems"
import OrderDetailDropdown from "./OrderDetailDropdown"
import OrderDetailsCheckoutButton from "./OrderDetailsCheckoutButton"
import OrderDetailsEditButton from "./OrderDetailsEditButton"
import OrderDetailsConfirmPaymentButton from "./OrderDetailsConfirmPaymentButton"
import OrderDetailsPaymentDetails from "./OrderDetailsPaymentDetails"
import OrderDetailsStatus from "./OrderDetailsStatus"
import OrderDetailsNote from "./OrderDetailsNote"
import OrderDetailsCoupons from "./OrderDetailsCoupons"
import OrderDetailsProblematicPaymentButton from "./OrderDetailsProblematicPaymentButton"
import OrderDetailsConfirmOrderButton from "./OrderDetailsConfirmOrderButton"
import OrderDetailReadyButton from "./OrderDetailReadyButton"
import OrderDetailLateButton from "./OrderDetailLateButton"
import OrderReadyViewedStatus from "./OrderReadyViewedStatus"
import classes from "./index.module.scss"

const OrderDetails = props => {
  const {
    order,
    setOrders,
    setOpenOrderDetailsSidebar
  } = props

  const i18n = useTranslation()[1]

  const dispatch = useDispatch()

  const isOnline =
    useSelector(state =>
      !state.internetStatus.forceOffline
    )

  const serviceTypeName =
    useSelector(state =>
      state.systemSettings.serviceTypeName
    )

  const [noPayments, setNoPayment] = useState(null)
  const [paymentDetail, setPaymentDetail] = useState(null)
  const [subPaymentItems, setSubPaymentItems] = useState(null)

  const unmount = useRef(false)

  const isOrderViewed = async () => {
    if (!isOnline) return

    if (order.isViewed) return

    setOrders(prev =>
      prev.map(o =>
        o._id === order._id
          ? { ...o, isViewed: true }
          : o
      )
    )

    dispatch(
      incNumberOfOrders(
        order.orderStatus,
        -1
      ))
  }

  const GetOrder = async () => {
    if (!isOnline) return

    try {
      const response =
        await getOrder(order._id)

      if (unmount.current) return

      if (!response.paymentDetail) {
        return setNoPayment(true)
      }

      setNoPayment(false)

      setPaymentDetail(
        response.paymentDetail
      )

      const subPaymentItems = {}

      response.paymentDetail.subPaymentItems.forEach(subPaymentItem => {
        const orderItemId = subPaymentItem.orderItemId

        subPaymentItems[orderItemId] = {
          subPaymentItemId: subPaymentItem._id,
          paymentStatus: subPaymentItem.paymentStatus
        }
      })

      setSubPaymentItems(subPaymentItems)
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    isOrderViewed()

    GetOrder()
  }, [isOnline])

  useEffect(() => {
    unmount.current = false

    return () => unmount.current = true
  }, [])

  const isOrderSplited =
    order.items.some(ti =>
      subPaymentItems?.[ti._id]?.paymentStatus === "paid"
    )

  const orderHasCoupon =
    order.couponData.some(c =>
      c.couponType === "basic"
    )

  const showEditButton =
    isOnline &&
    order.isCod &&
    !order.isOrderReady &&
    !orderHasCoupon &&
    !isOrderSplited &&
    haveAuth(["editOrder"]) &&
    (
      order.orderStatus === "pending" ||
      order.orderStatus === "notConfirmed"
    )

  const showCheckoutButton =
    order.isCod &&
    noPayments === false &&
    haveAuth(["checkout"]) &&
    order.orderStatus === "pending"

  const showConfirmPaymentButton =
    !order.isCod &&
    !!order.accountId &&
    noPayments === true &&
    haveAuth(["checkout"]) &&
    order.orderStatus === "pending" &&
    order.accountId !== order.addedByAccountId

  const isOrderAddedByAccess =
    noPayments === true &&
    !order.isCod &&
    !!order.accountId &&
    order.addedBySystem &&
    order.accountId !== order.addedByAccountId

  const showOrderReadyButton =
    !isOrderSplited &&
    haveAuth(["readyOrder"]) &&
    !order.isOrderReady &&
    (
      order.orderStatus === "pending" ||
      order.orderStatus === "paid"
    )

  const showOrderLateButton =
    !isOrderSplited &&
    haveAuth(["lateOrder"]) &&
    !order.isOrderReady &&
    !order.isOrderLate &&
    (
      order.orderStatus === "pending" ||
      order.orderStatus === "paid"
    )

  const showReadyAndLateButtons =
    serviceTypeName === "restaurant" &&
    (
      showOrderReadyButton ||
      showOrderLateButton
    )

  const isOrderNotConfirmed =
    order.isCod &&
    order.orderStatus === "notConfirmed"

  const isLtr = i18n.dir(i18n.language) === "ltr"

  return (
    <div className={classes["container"]}>
      <div className={classes["wrapper"]}>
        <div
          className={clsx(
            classes["header"],
            isLtr && classes["header-ltr"]
          )}
        >
          <OrderDetailDropdown
            order={order}
            setOrders={setOrders}
            paymentDetail={paymentDetail}
          />
        </div>

        <OrderDetailsStatus
          order={order}
        />

        <OrderReadyViewedStatus
          order={order}
        />

        <OrderDetailsHeader
          order={order}
          setOrders={setOrders}
        />

        {
          isOrderAddedByAccess &&
          <OrderDetailsNote
            order={order}
            setOrders={setOrders}
          />
        }

        {
          isOrderAddedByAccess &&
          <OrderDetailsPaymentDetails
            order={order}
            setOrders={setOrders}
          />
        }

        <OrderDetailsCustomerInfo
          order={order}
          isOrderAddedByAccess={isOrderAddedByAccess}
        />

        <OrderDetailsCoupons
          order={order}
        />

        <OrderDetailsItems
          order={order}
          subPaymentItems={subPaymentItems}
        />

        <OrderDetailsFooter
          order={order}
        />

      </div>
      {
        showReadyAndLateButtons &&
        <div className={classes["buttons-ready"]}>
          {
            showOrderLateButton &&
            <OrderDetailLateButton
              order={order}
              setOrders={setOrders}
            />
          }

          {
            showOrderReadyButton &&
            <OrderDetailReadyButton
              order={order}
              setOrders={setOrders}
            />
          }
        </div>
      }

      {
        showCheckoutButton &&
        <div className={classes["buttons"]}>
          {
            showEditButton &&
            <OrderDetailsEditButton
              order={order}
            />
          }
          <OrderDetailsCheckoutButton
            order={order}
            subPaymentItems={subPaymentItems}
          />
        </div>
      }

      {
        showConfirmPaymentButton &&
        <div className={classes["buttons"]}>
          <OrderDetailsConfirmPaymentButton
            order={order}
            setOrders={setOrders}
            setOpenOrderDetailsSidebar={setOpenOrderDetailsSidebar}
          />
          {
            !order.isPaymentProblematic &&
            <OrderDetailsProblematicPaymentButton
              order={order}
              setOrders={setOrders}
              setOpenOrderDetailsSidebar={setOpenOrderDetailsSidebar}
            />
          }
        </div>
      }

      {
        isOrderNotConfirmed &&
        <div className={classes["buttons"]}>
          {
            showEditButton &&
            <OrderDetailsEditButton
              order={order}
            />
          }

          <OrderDetailsConfirmOrderButton
            order={order}
            setOrders={setOrders}
            setOpenOrderDetailsSidebar={setOpenOrderDetailsSidebar}
          />
        </div>
      }
    </div>
  )
}

export default OrderDetails